import { createTheme, type ThemeOptions } from '@mui/material/styles'
import { type Theme } from '@mui/material/styles/createTheme'

const themeOptions: ThemeOptions = {
  typography: {
    allVariants: {
      fontFamily: 'Montserrat, sans-serif',
      fontSize: '20px',
    },
    title1: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '18px',
      lineHeight: '22px',
      display: 'block',
    },
    title2: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '20px',
      display: 'block',
    },
    title3: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '18px',
      display: 'block',
    },
    title4: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '16px',
      display: 'block',
    },
    title5: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      display: 'block',
    },
    title6: {
      fontFamily: 'Montserrat',
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '29px',
      display: 'block',
    },
    title7: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '26px',
      display: 'block',
    },
    title8: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '24px',
      lineHeight: '26px',
      display: 'block',
    },
    title9: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '28px',
      lineHeight: '36px',
      display: 'block',
    },
    title10: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
      display: 'block',
    },
    title11: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '56px',
      lineHeight: 'normal',
      display: 'block',
    },
    title12: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '40px',
      lineHeight: 'normal',
      display: 'block',
    },
    description1: {
      fontFamily: 'Montserrat',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '26px',
      display: 'block',
    },
    description2: {
      fontFamily: 'Montserrat',
      fontWeight: 500,
      fontSize: '10px',
      lineHeight: '12px',
      display: 'block',
    },
    description3: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '18px',
      lineHeight: '26px',
      display: 'block',
    },
    description4: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '22px',
      lineHeight: '26px',
      display: 'block',
    },
    paragraph: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: '24px',
      display: 'block',
    },
    subtitle1: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      display: 'block',
    },
    subtitle2: {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '16px',
      display: 'block',
    },
    subtitle3: {
      fontFamily: 'Montserrat',
      fontWeight: 300,
      fontSize: '14px',
      lineHeight: '19px',
      display: 'block',
    },
    subtitle4: {
      fontFamily: 'Montserrat',
      fontWeight: 300,
      fontSize: '18px',
      lineHeight: '19px',
      display: 'block',
    },
    subtitle5: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: '12px',
      display: 'block',
    },
  },
  palette: {
    mode: 'dark',
    blue900: { main: '#101426' },
    blue800: { main: '#151A30' },
    blue700: { main: '#182038' },
    blue600: { main: '#222B45' },
    blue500: { main: '#232E5C' },
    blue400: { main: '#234BC2' },
    blue300: { main: '#293450' },
    blue200: { main: '#5173B1' },
    blue100: { main: '#9EB9F5' },
    blue: { main: '#3366FF' },
    lightBlue: { main: '#B7C3DB' },
    lightBlue100: { main: '#B4C0D8' },
    gray: { main: '#ACB8D0' },
    white: { main: '#ffffff' },
    green: { main: '#70C1AC' },
    indigo300: { main: '#31528E' },
    silver: { main: '#d9d9d9' },
    charts: {
      indigo: { main: '#006A8D' },
      indigo100: { main: '#5C9ACA' },
      indigo200: { main: '#397C9D' },
      purple100: { main: '#598BFF' },
      purple: { main: '#5173B1' },
      green100: { main: '#70C1AC' },
      green200: { main: '#3DB19E' },
      orange400: { main: '#EA9A56' },
      orange: { main: '#FFB375' },
      orange200: { main: '#EB9A57' },
      yellow: { main: '#EDBC66' },
      red100: { main: '#E76F58' },
      red200: { main: '#D12D55' },
      red: { main: '#A9404A' },
      brown: { main: '#B45A3C' },
    },
  },
  breakpoints: {
    values: {
      sm: 1280,
      md: 1440,
      lg: 1920,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'large',
      },
      variants: [
        {
          props: { size: 'large' },
          style: {
            width: '247px',
            height: '35px',
          },
        },
        {
          props: { size: 'small' },
          style: {
            width: '130px',
            height: '30px',
          },
        },
      ],
    },
    MuiSnackbar: {
      defaultProps: {
        autoHideDuration: 2000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
            {
              WebkitAppearance: 'none',
            },
        },
      },
    },
  },
}

const customTheme: Theme = createTheme(themeOptions, {
  typography: {
    button: {
      fontWeight: 500,
      fontSize: '12px',
      lineHeight: '14px',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiSwitch: {
      styleOverrides: {
        height: 16,
        padding: 0,
        display: 'flex',
        '&:active': {
          thumb: {
            width: 18,
          },
          '& .MuiSwitch-switchBase.Mui-checked': {
            transform: 'translateX(9px)',
          },
        },
        '& .MuiSwitch-switchBase': {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(12px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
              backgroundColor: themeOptions.palette?.blue100.main,
            },
          },
        },
        thumb: {
          marginLeft: 3,
          marginTop: 4,
          background: 'white',
          boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
          width: 12,
          height: 12,
          borderRadius: 6,
          transition: themeOptions.transitions?.create?.(['width'], {
            duration: 200,
          }),
        },
        track: {
          borderRadius: 16 / 2,
          backgroundColor:
            themeOptions.palette?.mode === 'dark' ? 'rgba(255,255,255,.35)' : 'rgba(0,0,0,.25)',
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'large',
        variant: 'primary',
      },
      variants: [
        {
          props: { size: 'large' },
          style: {
            width: '247px',
            height: '35px',
          },
        },
        {
          props: { size: 'small' },
          style: {
            width: '130px',
            height: '30px',
          },
        },
        {
          props: { variant: 'primary' },
          style: {
            background: themeOptions.palette?.blue.main,
            borderRadius: '5px',
            '&:hover': {
              background: themeOptions.palette?.blue400.main,
            },
            '&:disabled': {
              background: themeOptions.palette?.blue300.main,
              color: themeOptions.palette?.gray.main,
            },
          },
        },
        {
          props: { variant: 'secondary' },
          style: {
            background: themeOptions.palette?.blue200.main,
            '&:hover': {
              background: themeOptions.palette?.indigo300.main,
            },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            background: 'transparent',
            borderColor: themeOptions.palette?.blue500.main,
            '& svg': {
              stroke: themeOptions.palette?.blue100.main,
            },
            '&:hover': {
              borderColor: themeOptions.palette?.blue400.main,
              backgroundColor: 'transparent',
              '& svg': {
                stroke: themeOptions.palette?.blue400.main,
              },
            },
            '&:disabled': {
              borderColor: themeOptions.palette?.blue300.main,
              '& svg': {
                stroke: themeOptions.palette?.blue300.main,
              },
            },
          },
        },
        {
          props: { variant: 'iconSecondary' },
          style: {
            background: themeOptions.palette?.blue500.main,
            '& svg': {
              fill: themeOptions.palette?.white.main,
            },
            '&:hover': {
              background: themeOptions.palette?.blue600.main,
            },
            '&:disabled': {
              background: themeOptions.palette?.blue600.main,
              opacity: 0.35,
              '& svg': {
                fill: themeOptions.palette?.blue200.main,
                opacity: 1,
              },
            },
          },
        },
        {
          props: { variant: 'iconTertiary' },
          style: {
            background: themeOptions.palette?.blue700.main,
            '& svg': {
              fill: themeOptions.palette?.white.main,
            },
            '&:hover': {
              background: themeOptions.palette?.blue600.main,
            },
            '&:disabled': {
              background: themeOptions.palette?.blue600.main,
              opacity: 0.35,
              '& svg': {
                fill: themeOptions.palette?.blue200.main,
                opacity: 1,
              },
            },
          },
        },
        {
          props: { variant: 'text' },
          style: {
            background: themeOptions.palette?.blue200.main,
            color: themeOptions.palette?.white.main,
            '& svg': {
              fill: themeOptions.palette?.white.main,
            },
            '&:hover': {
              background: themeOptions.palette?.indigo300.main,
            },
            '&:disabled': {
              background: themeOptions.palette?.blue300.main,
              color: themeOptions.palette?.gray.main,
              '& svg': {
                fill: themeOptions.palette?.blue200.main,
                opacity: 1,
              },
            },
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            background: themeOptions.palette?.blue600.main,
            color: themeOptions.palette?.white.main,
            '& svg': {
              fill: themeOptions.palette?.white.main,
            },
            '&:hover': {
              background: themeOptions.palette?.blue600.main,
            },
            '&:disabled': {
              background: themeOptions.palette?.blue300.main,
              color: themeOptions.palette?.gray.main,
              '& svg': {
                fill: themeOptions.palette?.blue200.main,
                opacity: 1,
              },
            },
          },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        standardSuccess: {
          backgroundColor: themeOptions.palette?.charts.green200.main,
          color: 'white',
        },
        standardError: {
          backgroundColor: themeOptions.palette?.charts.red.main,
          color: 'white',
        },
        standardWarning: {
          backgroundColor: themeOptions.palette?.charts.orange400.main,
          color: 'black',
        },
        standardInfo: {
          backgroundColor: themeOptions.palette?.charts.indigo.main,
          color: 'white',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 500,
          '&.Mui-error': {
            color: themeOptions.palette?.charts.red.main,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: themeOptions.palette?.blue700.main,
          '&:hover fieldset': {
            borderColor: themeOptions.palette?.blue100.main,
          },
          '&.Mui-focused fieldset': {
            borderColor: themeOptions.palette?.blue100.main,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: themeOptions.palette?.blue900.main,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: themeOptions.palette?.blue100.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeOptions.palette?.blue100.main,
          },
          '&.Mui-error': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: themeOptions.palette?.charts.red.main,
            },
          },
          border: '1px',
          borderRadius: '5px',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          borderColor: 'transparent',
          '& .MuiDataGrid-root': {
            borderColor: themeOptions.palette?.blue700.main,
          },
          '& .MuiDataGrid-columnHeaders': {
            minHeight: '44px !important',
            height: '44px',
            backgroundColor: themeOptions.palette?.blue700.main,
            borderColor: themeOptions.palette?.blue700.main,
            color: themeOptions.palette?.charts.purple100.main,
          },
          '& .MuiDataGrid-columnHeader': {
            pointerEvents: 'none',
            '&:checked': {
              borderColor: themeOptions.palette?.blue700.main,
            },
          },
          '& .MuiDataGrid-iconSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row': {
            maxHeight: '57px !important',
            height: '57px',
            '&:hover': {
              backgroundColor: themeOptions.palette?.blue700.main,
              color: themeOptions.palette?.charts.purple100.main,
              '& .MuiButton-iconTertiary': {
                background: themeOptions.palette?.blue600.main,
              },
            },
          },
          '& .MuiDataGrid-cell': {
            maxHeight: '57px !important',
            borderColor: themeOptions.palette?.blue700.main,
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cellContent': {
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
          },
          '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
            borderBottom: '1px solid',
            borderRight: '1px solid',
            borderColor: themeOptions.palette?.blue700.main,
          },
          '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell:first-of-type': {
            borderLeft: '1px solid',
            borderColor: themeOptions.palette?.blue700.main,
            '& .MuiDataGrid-cellContent': {
              marginLeft: '10px',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'transparent',
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '14px',
          },
          '& .MuiTablePagination-selectLabel': {
            display: 'none',
          },
          '& .MuiButton-primary': {
            display: 'none',
          },
          '& .MuiButtonBase-root': {
            height: '20px',
            width: '20px',
            '&:hover': {
              borderRadius: '5px',
            },
          },
          '& .MuiInputBase-root': {
            '&.Mui-focused:after': {
              borderBottom: 'none',
            },
          },
          '& .MuiInputBase-root:after': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: themeOptions.palette?.charts.purple100,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: themeOptions.palette?.blue700.main,
          fontSize: '14px',
          maxWidth: '200px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: themeOptions.palette?.gray.main,
          '&.Mui-selected': {
            color: themeOptions.palette?.blue100.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: themeOptions.palette?.blue100.main,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        noOptions: {
          backgroundColor: themeOptions.palette?.blue700.main,
        },
        endAdornment: {
          top: 'auto',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 1499,
        },
        paper: {
          borderRadius: '10px',
          backgroundColor: themeOptions.palette?.blue600.main,
          backgroundImage: 'none',
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          zIndex: 1599,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: themeOptions.palette?.blue600.main,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          flexDirection: 'row-reverse',
          gap: '16px',
          borderBottom: '1px solid',
          borderColor: themeOptions.palette?.blue800.main,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: '24px',
        },
      },
    },
  },
})

export default customTheme
